import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api-client';
import type { DepartmentDto, EmploymentStatusDto, JobTitleDto, LocationDto } from './types';
import type { SelectOption } from '../components/InlineAddSelect';

type BucketOptionView = { id: number; name: string };

const JOB_TITLES_KEY = ['organization', 'job-titles'] as const;
const LOCATIONS_KEY = ['organization', 'locations'] as const;

export function useDepartments() {
  return useQuery({
    queryKey: ['organization', 'departments'],
    queryFn: () => api.get<DepartmentDto[]>('/v1/organization/departments'),
  });
}

export function useLocations() {
  return useQuery({
    queryKey: LOCATIONS_KEY,
    queryFn: () => api.get<LocationDto[]>('/v1/organization/locations'),
  });
}

export function useJobTitles() {
  return useQuery({
    queryKey: JOB_TITLES_KEY,
    queryFn: () => api.get<JobTitleDto[]>('/v1/organization/job-titles'),
  });
}

export function useAddJobTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<SelectOption> => {
      const created = await api.post<BucketOptionView>(
        '/v1/settings/employee-fields/JOB_TITLE/bucket-options',
        { name },
      );
      return { id: created.id, label: created.name };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: JOB_TITLES_KEY });
    },
  });
}

export function useAddLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<SelectOption> => {
      const created = await api.post<BucketOptionView>(
        '/v1/settings/employee-fields/LOCATION/bucket-options',
        { name },
      );
      return { id: created.id, label: created.name };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCATIONS_KEY });
    },
  });
}

const EMPLOYMENT_STATUSES_KEY = ['organization', 'employment-statuses'] as const;

export function useEmploymentStatuses() {
  return useQuery({
    queryKey: EMPLOYMENT_STATUSES_KEY,
    queryFn: () => api.get<EmploymentStatusDto[]>('/v1/organization/employment-statuses'),
  });
}

// Admin-only creation of a new employment status. Invalidates the read list so
// the new entry appears immediately and maps the created status to SelectOption shape.
export function useAddEmploymentStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<SelectOption> => {
      const created = await api.post<EmploymentStatusDto>(
        '/v1/organization/employment-statuses',
        { name },
      );
      return { id: created.id, label: created.name };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: EMPLOYMENT_STATUSES_KEY });
    },
  });
}

export function useOrgLookups() {
  const departments = useDepartments();
  const locations = useLocations();
  const jobTitles = useJobTitles();
  const employmentStatuses = useEmploymentStatuses();
  return { departments, locations, jobTitles, employmentStatuses };
}
